import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { NotFound } from '../layout/SystemState';
import { getRun } from '../../services/runService';
import { BackToRuns, Bar, Card, PageTitle, RunStatusChip, fmt, shortDate } from './parts';

const Fact = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 2, py: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{children}</Typography>
    </Box>
);

const CountLine = ({ label, value, total, color }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 120px 1fr' }, gap: { xs: 0.5, sm: 2 }, alignItems: 'center', py: 0.75 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(value)} of {fmt(total)}</Typography>
        <Bar value={value} total={total} color={color} />
    </Box>
);

// /runs/:runId (wf-run-export 3–4, YES-260, YES-268): the run's only page once it has started. Counts
// only: no codes, utterances or transcript text. A run in setup reopens its wizard.
const short = (email) => (email ? email.split('@')[0] : '—');
const FLAG_ORDER = ['HD', 'CD', 'HU', 'control'];

// Each ADAS lesson's stage in words (wf-research-run-setup-4): counts only, no codes
const annotationText = ({ annotators, submitted, opened, coded, total }) => {
    if (submitted >= annotators) return annotators > 1 ? `Submitted (${submitted} of ${annotators})` : 'Submitted';
    if (!opened) return 'Not opened';
    if (annotators > 1 && submitted > 0) return `Draft · ${submitted} of ${annotators} submitted`;
    return `${fmt(coded ?? 0)} of ${fmt(total)} utterances`;
};
const flagsText = (row) => (row.flags
    ? FLAG_ORDER.filter((f) => row.flags[f] > 0).map((f) => `${f} ${row.flags[f]}`).join(' · ')
    : row.annotation.annotators > 1 ? 'after both annotators' : 'after annotation');
const adjudicationText = (row) => {
    const a = row.adjudication;
    if (!a) return '—';
    if (a.final) return 'Done · final layer written';
    if (!a.blindLocked) return `Queued: ${fmt(a.blindTotal)} codes`;
    return `${fmt(a.blindLocked)} of ${fmt(a.blindTotal)} codes${a.tiesWaiting ? ` · ${a.tiesWaiting} ${a.tiesWaiting === 1 ? 'tie' : 'ties'} with ${row.tieBreakSlot}` : ''}`;
};

const AdasLessons = ({ rows }) => {
    const inAdjudication = rows.filter((r) => r.adjudication && !r.adjudication.final).length;
    const finalized = rows.filter((r) => r.adjudication?.final).length;
    return (
        <Card title="Lessons">
            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Lessons in this run" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>{['Lesson', 'Annotation', 'Flags', 'Adjudication', 'People'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.lessonId}>
                                <TableCell sx={{ fontWeight: 700 }}>{r.code}</TableCell>
                                <TableCell>{annotationText(r.annotation)}</TableCell>
                                <TableCell sx={{ color: r.flags ? 'text.primary' : 'text.secondary' }}>{flagsText(r)}</TableCell>
                                <TableCell sx={{ color: r.adjudication?.final ? 'success.dark' : 'text.primary' }}>{adjudicationText(r)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.annotatorAccounts.map(short).join(', ')} · {(r.blindSlots || []).join(' ')} · {r.tieBreakSlot || '—'}</TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>No lessons yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Box>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                {rows.length} {rows.length === 1 ? 'lesson' : 'lessons'} · {inAdjudication} in adjudication, {finalized} finalized. Items join the adjudicators’ queues as soon as a lesson’s flags are computed; there is no release step in an ADAS run.
            </Typography>
            <Typography variant="caption" component="p" color="text.secondary">People: annotator(s) · adjudicators · tie-breaker. Flag counts are researcher-only.</Typography>
        </Card>
    );
};

const RunSummary = () => {
    const { runId } = useParams();
    const { can } = useAuth();
    const [run, setRun] = useState(null);
    const [status, setStatus] = useState('loading');

    const load = useCallback(async () => {
        setStatus('loading');
        try {
            setRun(await getRun(runId));
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [runId]);

    useEffect(() => {
        load();
    }, [load]);

    if (status === 'notFound') return <NotFound />;
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the run.</Alert>
            </Box>
        );
    }
    if (run?.status === 'setup') return <Navigate to={`/runs/${encodeURIComponent(runId)}/setup`} replace />;

    const p = run?.progress || {};
    const both = (p.agreed || 0) + (p.tied || 0);

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <BackToRuns />
            <PageTitle
                aside={run?.kind === 'adas'
                    ? can('lesson.research') && <Button variant="outlined" component={RouterLink} to={`/runs/${encodeURIComponent(runId)}/add`}>Add lessons</Button>
                    : run?.status === 'open' && (
                        <Button variant="outlined" component={RouterLink} to={`/runs/${encodeURIComponent(runId)}/setup`}>Edit assignment</Button>
                    )}
            >
                <Typography variant="h2" component="h1">{run ? run.name : <Skeleton width={300} />}</Typography>
                {run && <RunStatusChip status={run.status} />}
            </PageTitle>
            <Divider sx={{ my: 2.5 }} />

            {!run ? <Skeleton variant="rounded" height={420} /> : (
                <Grid container spacing={2.5} alignItems="flex-start">
                    {run.kind === 'adas' && (
                        <Grid item xs={12}>
                            <AdasLessons rows={run.lessonRows || []} />
                        </Grid>
                    )}
                    <Grid item xs={12} lg={8}>
                        <Card title="Progress">
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                                    <Typography variant="h5" component="h3" color="primary.main">All items</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {fmt(p.items)} items{run.startedAt ? ` · served since ${shortDate(run.startedAt)}` : ''}
                                    </Typography>
                                </Box>
                                <CountLine label="Blind codes locked" value={p.blindLocked} total={p.blindTotal} />
                                <CountLine label="Final labels" value={p.final} total={p.items} color="success" />
                                {both > 0 && (
                                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                                        Of {fmt(both)} items with both blind codes: {fmt(p.agreed)} agreed, {fmt(p.tied)} tied ({fmt(p.tiesBroken)} broken, {fmt(p.tiesWaiting)} waiting for their tie-breaker).
                                    </Typography>
                                )}
                            </Box>

                            <Typography variant="overline" component="h3" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>By slot</Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" aria-label="Progress by slot" sx={{ minWidth: 620 }}>
                                    <TableHead>
                                        <TableRow>
                                            {['Slot', 'Account', 'Blind codes locked', '', 'Ties broken', 'Ties waiting', 'Last activity'].map((h, i) => <TableCell key={i}>{h}</TableCell>)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(run.bySlot || []).map((s) => (
                                            <TableRow key={s.name}>
                                                <TableCell sx={{ fontWeight: 700 }}>{s.name}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{s.account ? s.account.split('@')[0] : 'unmapped'}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(s.blindLocked)} of {fmt(s.blindTotal)}</TableCell>
                                                <TableCell sx={{ width: 120 }}><Bar value={s.blindLocked} total={s.blindTotal} width={100} /></TableCell>
                                                <TableCell>{fmt(s.tiesBroken ?? 0)}</TableCell>
                                                <TableCell sx={{ color: s.tiesWaiting ? 'warning.dark' : 'text.primary', fontWeight: s.tiesWaiting ? 600 : 400 }}>{fmt(s.tiesWaiting ?? 0)}</TableCell>
                                                <TableCell>{shortDate(s.lastActivityAt) || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                                Blind codes: the slot’s queued items it has locked. Ties: tie items of lessons the slot breaks ties for. Last activity: when the slot last locked a blind code or a tie.
                            </Typography>

                            <Typography variant="overline" component="h3" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>By flag</Typography>
                            <Table size="small" aria-label="Progress by flag">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Flag</TableCell>
                                        <TableCell>Items</TableCell>
                                        <TableCell>Final labels</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(run.byFlag || []).filter((f) => f.items > 0).map((f) => (
                                        <TableRow key={f.flag}>
                                            <TableCell sx={{ fontWeight: 700 }}>{f.flag}</TableCell>
                                            <TableCell>{fmt(f.items)}</TableCell>
                                            <TableCell>{fmt(f.final)}</TableCell>
                                            <TableCell sx={{ width: 140 }}><Bar value={f.final} total={f.items} color="success" width={120} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Card title="Run">
                            <Fact label="Kind">{run.kind}</Fact>
                            {run.bundle && <Fact label="Bundle">{run.bundle.externalId}{run.bundle.committedAt ? ` · imported ${shortDate(run.bundle.committedAt)}` : ''}</Fact>}
                            <Fact label="Lessons">
                                {fmt(run.lessons)}{typeof run.lessonsAssigned === 'number' ? (run.lessonsAssigned === run.lessons ? ' · all assigned' : ` · ${fmt(run.lessonsAssigned)} assigned`) : ''}
                            </Fact>
                            <Fact label="Utterances">{fmt(run.utterances)}</Fact>
                            <Fact label="Items">{fmt(p.items)}</Fact>
                            {run.schedule && <Fact label="Schedule">{run.schedule}</Fact>}
                            <Fact label="Started">{shortDate(run.startedAt) || '—'}</Fact>
                            {run.closedAt && <Fact label="Closed">{shortDate(run.closedAt)}</Fact>}
                            <Fact label="Codebook">{run.codebookVersion || '—'}</Fact>
                            <Fact label="Last export">{shortDate(run.lastExportAt) || 'none yet'}</Fact>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default RunSummary;
